import React, {useEffect, useMemo, useState} from 'react'
import {useDropzone} from 'react-dropzone'
import {CloudUpload as CloudUploadIcon, ErrorOutline} from '@mui/icons-material'
import {
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import {
  Accordion,
  Button,
  Dialog,
  FullScreenDialog,
  IconButton,
  Loading,
} from 'finsys-webcomponent'
import * as ExcelJS from 'exceljs'
import {saveAs} from 'file-saver'
import moment from 'moment'

import {useSnackBar} from 'hoc/SnackbarHandler'

import './index.scss'
import {dateTimeFormat, dateTimeFormatTableForFile} from '../../_helper/_constants'
type ImportExcelProps = {
  label: string,
  templateUrl: string,
  open: boolean,
  handleClose: () => void,
  columns: Array<{
    Header: string,
    accessor: string,
    width?: string,
  }>,
  handleExcelUpload: () => void,
  handleAdditionalWorksheet?: () => void | null,
}

const processRichText = (richText) => {
  let htmlContent = ''
  richText.forEach((segment) => {
    let segmentText = segment.text || ''
    let segmentHtml = segmentText

    // Apply font styles if any
    if (segment.font) {
      const font = segment.font

      // Apply bold style
      if (font.bold) {
        segmentHtml = `<b>${segmentHtml}</b>`
      }

      // Apply italic style
      if (font.italic) {
        segmentHtml = `<i>${segmentHtml}</i>`
      }

      // Apply underline style
      if (font.underline) {
        segmentHtml = `<u>${segmentHtml}</u>`
      }
    }

    // Add the processed segment to the HTML content
    htmlContent += segmentHtml
  })

  // Handle line breaks: Convert \n to <br> tags
  htmlContent = htmlContent.replace(/\n/g, '<br>')

  return `<p>${htmlContent}</p>`
}

/**
 * Generic Excel Upload component
 * @param uploadFiles
 * @param columns
 * @return {JSX.Element}
 * @constructor
 */
export function ImportExcel({
  open = false,
  handleClose,
  columns,
  handleExcelUpload,
  handleAdditionalWorksheet = null,
  isLoading = false,
  label,
}: ImportExcelProps) {
  const {setSnackBar} = useSnackBar()
  const [openCancelImportDialog, setOpenCancelImportDialog] = useState({
    open: false,
  })
  const [errorMessage, setErrorMessage] = useState('')
  const [data, setData] = useState([])
  const [result, setResult] = useState({
    totalRows: null,
    validRows: null,
    invalidRows: null,
    errorRowsWithError: {},
    name: null,
  })

  const [isUploading, setIsUploading] = useState(false)

  const [page, setPage] = React.useState(0)
  const [rowsPerPage, setRowsPerPage] = React.useState(10)

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value)
    setPage(0)
  }

  const defaultColumns = useMemo(() => [...columns], [columns])

  /**
   * Handles Drag and drop and uploading of files
   */
  const {
    getRootProps,
    open: openFile,
    getInputProps,
  } = useDropzone({
    maxFiles: 1,
    accept: {
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
    // Disable click and keydown behavior
    noClick: true,
    noKeyboard: true,
    onDrop: async (files, error) => {
      if (files.length > 0) {
        setIsUploading(true)
        setErrorMessage('')

        if (!files || files.length === 0) return

        const file = files[0]

        // Initialize a new ExcelJS workbook
        const workbook = new ExcelJS.Workbook()

        // Load the Excel file
        await workbook.xlsx.load(file)

        // Access the first sheet (assuming we're working with the first sheet)
        const worksheet = workbook.worksheets[0]

        // Read headers from the first row
        const headerRow = worksheet.getRow(1).values // Get the first row values (header row)

        // Create a header object for easy lookup
        const headerObject = headerRow.reduce((acc, header, index) => {
          if (header) {
            // Ensure header is not undefined or null
            acc[header] = {index}
          }
          return acc
        }, {})

        // Set up the result and clear the data
        setResult({
          totalRows: null,
          validRows: null,
          invalidRows: null,
          errorRowsWithError: {},
          name: null,
        })
        setData([])

        // Set the name of the uploaded file (without extension)
        setResult((prevState) => ({
          ...prevState,
          name: file.name?.split('.').slice(0, -1).join('.'),
        }))

        // List of expected columns (adjust as needed)
        const totalColumn = [...columns] // Assuming `columns` is predefined
        let missingColumns = []

        // Check if all expected columns exist in the header
        totalColumn.forEach(({Header, accessor, required}) => {
          if (!headerObject[accessor]) {
            missingColumns.push(accessor)
            setErrorMessage(`Invalid template! ${missingColumns.toString()} Missing`)
            setIsUploading(false)
            throw new Error('Invalid Excel Template File')
          }
        })

        // Read data rows (starting from the second row, excluding the header)
        const rows = []
        worksheet.eachRow((row, rowNumber) => {
          if (rowNumber > 1) {
            // Skip the header row
            rows.push(row)
          }
        })

        // Process the data rows
        processHTMLExcel(rows, totalColumn, headerObject, setData)
      } else {
        setSnackBar({
          open: true,
          type: 'error',
          message: `You can only upload 1 file and file type must be .xls, .xlsx`,
        })
      }
    },
  })

  const isRichText = (cell) => {
    return Array.isArray(cell?.richText) && cell?.richText.length > 0
  }

  // Helper function to extract cell style (bold, underline)
  const getCellStyle = (cell) => {
    const font = cell?.style?.font || {}
    return {
      bold: font.bold ? '<b>' : '',
      underline: font.underline ? '<u>' : '',
    }
  }

  // Helper function to format the cell value with applied styles
  const formatCellValue = (cellValue, styles) => {
    let formattedValue = cellValue ? cellValue.toString().replace(/\n/g, '<br>') : ''
    if (styles.bold || styles.underline) {
      formattedValue = `${styles.bold}${styles.underline}${formattedValue}${
        styles.bold ? '</b>' : ''
      }${styles.underline ? '</u>' : ''}`
    }
    return formattedValue
  }

  // Helper function to validate a single cell
  const validateCell = ({value, dependentFieldValue, toValidate, accessor, row}) => {
    if (!toValidate) return null

    const trimmedValue = value?.trim() ?? ''
    const trimmedDependentFieldValue = dependentFieldValue?.trim() ?? ''

    return toValidate({
      data: row,
      value: trimmedValue,
      dependentField: trimmedDependentFieldValue,
      accessor,
    })
  }

  // Process the rows of Excel data
  function processHTMLExcel(rows, columns, headerObject, setData) {
    let errorRowsWithError = {}

    // Process each row and map columns to their data
    const newRows = rows.map((row, rowIndex) => {
      const rowData = {}
      let isRowValid = true

      // Process each column in the row
      columns.forEach(
        ({accessor, toValidate, required = false, dependentField = null}) => {
          let error = []
          const cell = row.getCell(headerObject[accessor].index)

          // Get the cell value (formatted) and styles (bold, underline)
          const cellValue = isRichText(cell.value)
            ? processRichText(cell.value.richText)
            : cell.text
          const cellStyle = getCellStyle(cell)
          const formattedValue = formatCellValue(cellValue, cellStyle)

          // Retrieve dependent field value and style if applicable
          let dependentFieldValue = null
          let dependentFieldStyle = null
          if (dependentField) {
            const dependentCell = row.getCell(headerObject[dependentField].index)
            dependentFieldValue = dependentCell ? dependentCell.value : null
            dependentFieldStyle = getCellStyle(dependentCell)
          }

          // Validate cell value
          const validationResult = validateCell({
            value: formattedValue,
            dependentFieldValue,
            dependentFieldStyle,
            toValidate,
            accessor,
            row,
          })

          if (validationResult) {
            error.push(validationResult)
            isRowValid = false
          }

          if (!isRowValid) {
            errorRowsWithError[rowIndex] = {
              ...errorRowsWithError[rowIndex],
              [accessor]: error,
            }
          }

          rowData[accessor] = formattedValue
        }
      )

      return rowData
    })

    // Filter out rows with errors and count valid rows
    const totalRows = newRows.length
    const validRows = totalRows - Object.keys(errorRowsWithError).length
    const filteredRows = newRows.filter((_, index) => !errorRowsWithError[index])

    // Update the state with processed data and error statistics
    setData(filteredRows)
    setResult((prevState) => ({
      ...prevState,
      totalRows,
      validRows: validRows <= 0 ? 0 : validRows,
      invalidRows: Object.keys(errorRowsWithError).length,
      errorRowsWithError,
    }))

    // Finish uploading process
    setIsUploading(false)

    // Show success message in the SnackBar
    setSnackBar({
      open: true,
      type: 'success',
      message: `Loaded`,
    })
  }

  /**
   * To handle the error log
   */
  const writeErrorExcel = () => {
    let error = []
    // let rowNumber = 1
    // lets format the data first
    for (const [rowKey, rowValue] of Object.entries(result.errorRowsWithError)) {
      let rowNumber = parseInt(rowKey) + 2

      for (const [fieldName, value] of Object.entries(rowValue)) {
        value.forEach((err) => {
          error.push([rowNumber, fieldName, err])
        })
      }
    }
    // Create workbook & add worksheet
    const workbook = new ExcelJS.Workbook()
    const worksheet = workbook.addWorksheet('error_log')

    // add column headers
    worksheet.columns = [
      {header: 'Row Number', key: 'rowNumber', width: 15},
      {header: 'Field Name', key: 'fieldName', width: 50},
      {header: 'Error', key: 'error', width: 100},
    ]

    worksheet.addRows(error)

    worksheet.eachRow(function (row, rowNumber) {
      row.eachCell((cell, colNumber) => {
        if (rowNumber == 1) {
          // First set the background of header row
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: {argb: 'FFFF00'},
          }
        }
        // Set border of each cell
        cell.border = {
          top: {style: 'thin'},
          left: {style: 'thin'},
          bottom: {style: 'thin'},
          right: {style: 'thin'},
        }
      })
      //Commit the changed row to the stream
      row.commit()
    })

    if (handleAdditionalWorksheet) {
      handleAdditionalWorksheet(workbook)
    }

    const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    const fileExtension = '.xlsx'
    // save workbook to disk
    workbook.xlsx
      .writeBuffer('sample.xlsx')
      .then((data) => {
        const blob = new Blob([data], {type: fileType})

        saveAs(
          blob,
          `${moment().format(dateTimeFormatTableForFile)}_poa_bam_data_error_log` +
            fileExtension
        )
      })
      .catch((err) => {
        console.log('err', err)
      })
  }

  /**
   * To delete rows after upload
   * @param index
   */
  const handleDeleteRow = (index) => {
    setData((prevState) => {
      const rows = prevState
      rows.splice(index, 1)

      return [...rows]
    })
  }

  /**
   * To handle cancel
   */
  const handleCancelImport = () => {
    if (data.length > 1 && !isLoading) {
      setOpenCancelImportDialog({
        open: true,
      })
    } else {
      handleClose()
    }

    handleResetImport()
    setIsUploading(false)
  }

  const handleResetImport = () => {
    setData([])
    setResult({
      totalRows: null,
      validRows: null,
      invalidRows: null,
      errorRowsWithError: {},
      name: null,
    })
    setErrorMessage('')
  }
  /**
   * To handle upload excel
   */
  const handleUpload = () => {
    if (data.length === 0) {
      setSnackBar({
        open: true,
        type: 'error',
        message: `No data to upload`,
      })
      return
    }
    handleExcelUpload(data)
    handleClose()
  }

  /**
   * To avoid screen being stale as we are processing large chunk of datra
   */
  useEffect(() => {
    if (!isLoading) {
      handleClose()
      handleResetImport()
      handleClose()
    }
  }, [isLoading])

  return (
    <FullScreenDialog
      open={open}
      handleClose={() => {
        handleCancelImport()
        handleResetImport()
      }}
      title={`Import Excel for ${label}`}
      footer={
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-end"
          spacing={2}
        >
          <Button
            label={'Cancel'}
            type={'text'}
            onClick={() => {
              handleCancelImport()
              handleResetImport()
            }}
            disabled={isLoading}
          />
          <Button
            label={'Confirm Import'}
            onClick={handleUpload}
            disabled={isLoading || errorMessage || result.invalidRows > 0}
          />
        </Stack>
      }
    >
      {openCancelImportDialog.open && (
        <Dialog
          type={'warning'}
          disableBackdropClick
          title={'Confirmation'}
          content={
            'Are you sure you want cancel this import, you have data yet to be uploaded.'
          }
          buttons={[
            {
              label: 'Cancel',
              onClick: () => {
                setOpenCancelImportDialog({open: false})
              },
              type: 'text',
            },
            {
              label: 'Okay',
              onClick: () => {
                setOpenCancelImportDialog({open: false})
                handleClose()
              },
              type: 'primaryOutline',
            },
          ]}
        />
      )}
      <Grid container>
        <Grid item xs={12} sx={{pb: 1}}>
          <Accordion title={'Select an excel file'}>
            {isUploading || isLoading ? (
              <>
                <Loading
                  open={true}
                  fullScreen={false}
                  withImage={false}
                  sx={{margin: '8vh auto'}}
                />
                {isUploading && (
                  <Stack>
                    <Typography variant={'caption'} align={'center'} gutterBottom>
                      Please be patient, while we process and validate your data. It might
                      take awhile if there are a lot of rows.
                    </Typography>
                  </Stack>
                )}
                {isLoading && (
                  <Stack>
                    <Typography variant={'caption'} align={'center'} gutterBottom>
                      Please be patient, while we are uploading ur data. It might take
                      awhile if there are a lot of rows.
                    </Typography>
                  </Stack>
                )}
              </>
            ) : (
              <Grid
                sx={{pb: 2}}
                item
                xs={12}
                sm={12}
                md={12}
                lg={12}
                justifyContent={'space-between'}
              >
                <div
                  {...getRootProps({className: 'dropzone'})}
                  style={{
                    height: '12',
                    border: '2px dotted',
                    textAlign: 'center',
                  }}
                >
                  <input {...getInputProps()} />
                  <div>
                    <Stack
                      direction="column"
                      justifyContent="center"
                      alignItems="center"
                      spacing={1}
                      sx={{p: 7}}
                    >
                      <Stack
                        direction="row"
                        justifyContent="center"
                        alignItems="center"
                        spacing={1}
                      >
                        <CloudUploadIcon />
                        <span>Drag and drop or</span>
                        <Button
                          size="small"
                          type={'secondary'}
                          label={'Select from your computer'}
                          onClick={openFile}
                        />
                      </Stack>
                      <Typography variant={'caption'} align={'left'}>
                        <ul>
                          <li>To upload 1 file at the time, accept .xls/.xlsx only</li>
                        </ul>
                      </Typography>
                    </Stack>
                  </div>
                </div>
              </Grid>
            )}
          </Accordion>
        </Grid>

        <Grid item xs={12} sm={12} md={12} lg={12}>
          <Stack
            direction={{xs: 'column'}}
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Stack direction={'row'} justifyContent="space-between" sx={{width: '100%'}}>
              <Typography variant={'subtitle2'} align={'left'} sx={{fontWeight: '700'}}>
                Total Rows in Excel ({result.totalRows ?? 0}) | Valid Rows (
                {result.validRows ?? 0}) | Invalid Rows ({result.invalidRows ?? 0})
                {errorMessage && (
                  <span style={{fontSize: '1rem', color: 'red'}}> {errorMessage}</span>
                )}
              </Typography>
            </Stack>

            {result.invalidRows > 0 && (
              <IconButton
                title={''}
                handleOnClick={() => writeErrorExcel()}
                sx={{float: 'left', padding: 0, borderRadius: 0}}
              >
                <ErrorOutline color={'error'} fontSize={'small'} />
                <Typography
                  variant={'overline'}
                  sx={{pl: 1, fontSize: 12}}
                  color={'error'}
                >
                  Click here to download error log
                </Typography>
              </IconButton>
            )}
          </Stack>

          <Paper sx={{width: '100%', overflow: 'hidden'}}>
            <TableContainer sx={{maxHeight: `calc(100vh - 250px)`}}>
              <Table stickyHeader aria-label="sticky table" className={'import-excel'}>
                <TableHead>
                  <TableRow>
                    {defaultColumns.map((column, dataIndex) => (
                      <TableCell
                        padding={'checkbox'}
                        key={`${column.Header}-${dataIndex}`}
                        style={{minWidth: column.width}}
                      >
                        {column.Header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, dataIndex) => {
                      const renderCellContent = (row, column) => {
                        const cellValue = row[column.accessor]

                        // Directly use `dangerouslySetInnerHTML` to render HTML
                        return (
                          <div
                            dangerouslySetInnerHTML={{
                              __html: cellValue, // Assuming `cellValue` is already HTML
                            }}
                          />
                        )
                      }

                      return (
                        <TableRow
                          hover
                          role="checkbox"
                          tabIndex={-1}
                          key={`row-${dataIndex}`}
                        >
                          {defaultColumns.map((column, index) => {
                            return (
                              <TableCell
                                padding={'checkbox'}
                                key={`${column.index}-${index}`}
                              >
                                {/*{row[column.accessor]}*/}
                                {renderCellContent(row, column)}
                              </TableCell>
                            )
                          })}
                        </TableRow>
                      )
                    })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 100]}
              component="div"
              count={data.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        </Grid>
      </Grid>
    </FullScreenDialog>
  )
}
